/**
 * JP trade-list screens — Sweet and Spicy Trade Recs (`docs/plans/jp-recs.md`).
 *
 * A screen whose `ScreenerConfig.recs` is set asks `qf-scr` for the trade at one
 * frequency, and gets `rec*` columns back: `recDir` is +1 for a long, -1 for a
 * short, empty for a row that is not a recommendation. Long and Short are
 * therefore **one request and a filter**: switching side costs no round trip,
 * and the frequency is the only part the server is told.
 */

import type { ScreenerFilterNode, ScreenerRecs } from "@qufolio/model";
import type { ScreenerFilter } from "@qufolio/screener-grid";
import type { ScreenerFrame } from "@qufolio/transport";

/** The column the server marks each row's side in. */
export const RECS_DIRECTION_FIELD = "recDir";

export const RECS_FREQUENCIES: readonly ScreenerRecs["frequency"][] = ["d", "w", "m"];
export const RECS_DIRECTIONS: readonly NonNullable<ScreenerRecs["direction"]>[] = ["long", "short"];

/** What the scan socket is told: the side is the browser's, so it is left out. */
export interface RecsAsk {
  kind: ScreenerRecs["kind"];
  frequency: ScreenerRecs["frequency"];
}

export function recsAsk(recs: ScreenerRecs | undefined): RecsAsk | undefined {
  return recs ? { kind: recs.kind, frequency: recs.frequency } : undefined;
}

/** A value signature for the socket's key — empty for an ordinary screen. */
export function recsKey(recs: ScreenerRecs | undefined): string {
  return recs ? `${recs.kind}:${recs.frequency}` : "";
}

/**
 * `filter` with the chosen side ANDed in, on the way to the grid only.
 *
 * **Every row stays in the frame**, recommendation or not, and this is what
 * shows one side: a row with no `recDir` matches neither, which is how a stock
 * that is not a recommendation this bar disappears from both lists. Applied
 * even with the panel's filters switched off — the side is which list you are
 * reading, not a condition you can suspend.
 */
export function withRecsDirection(
  filter: ScreenerFilter | undefined,
  recs: ScreenerRecs | undefined,
): ScreenerFilter | undefined {
  if (!recs) return filter;
  const side: ScreenerFilterNode = {
    field: RECS_DIRECTION_FIELD,
    op: "eq",
    value: recs.direction === "short" ? -1 : 1,
  };
  if (!filter) return side;
  return { op: "all", children: [filter as ScreenerFilterNode, side] };
}

/** What the server sends for a Recs screen (`jp_recs.OUTPUTS`, qv-data). */
export const RECS_OUTPUT_FIELDS: readonly string[] = [
  "recDir", "recChange", "recChangePct", "recVolume", "recVolDelta", "recPmc",
  "recEntry", "recTarget", "recTargetPct", "recStop", "recStopPct", "recAge",
  "recTechRank", "recRank",
];

/**
 * `frames` with every `rec*` cell emptied — for when the bar or kind changes.
 *
 * The answer to the previous question is a plausible wrong list, not a stale
 * one: until the new bar's push lands (its inputs may still be computing, and a
 * pending push carries no `rec*` to overwrite with) the grid would show W's
 * trades under the D button. Empty reads as "nothing yet", which is true.
 */
export function withoutRecs(frames: readonly ScreenerFrame[]): readonly ScreenerFrame[] {
  const recs = new Set(RECS_OUTPUT_FIELDS);
  let changed = false;
  const out = frames.map((frame) => {
    const names = frame.fields.map((f) => f.name).filter((n) => recs.has(n));
    if (frame.name !== "detail" || names.length === 0) return frame;
    changed = true;
    return {
      ...frame,
      rows: frame.rows.map((row) => {
        const next = { ...row };
        for (const name of names) next[name] = null;
        return next;
      }),
    };
  });
  return changed ? out : frames;
}

/** The toolbar's spelling of a frequency. */
export const RECS_FREQUENCY_LABEL: Record<ScreenerRecs["frequency"], string> = { d: "D", w: "W", m: "M" };
